import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Plus, ChevronDown, ChevronRight, MessageSquare, UserPlus, Check, GripVertical } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { clsx } from "clsx";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { formatDate } from "@/utils/formatDate";
import {
  useTasks,
  useUpdateTask,
  useAssignUser,
  useUnassignUser,
  useReorderTasks,
  useSetTaskExpanded,
  useTaskGroupStates,
  useSetGroupCollapsed,
} from "@/features/tasks/hooks/useTasks";
import { useTaskCategories } from "@/features/tasks/hooks/useTaskCategories";
import { CategoryFilter } from "@/features/tasks/components/CategoryFilter";
import { useProjects } from "@/features/tasks/hooks/useProjects";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useClientsForTasks } from "@/features/tasks/hooks/useClients";
import { useAllSubTasks } from "@/features/tasks/hooks/useSubTasks";
import { TaskDetailPanel } from "@/features/tasks/components/TaskDetailPanel";
import { SubTaskDetailPanel } from "@/features/tasks/components/SubTaskDetailPanel";
import { CreateTaskModal } from "@/features/tasks/components/CreateTaskModal";
import { WorkProgressSection } from "@/features/tasks/components/WorkProgressSection";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  TASK_STATUS_LABELS,
  TASK_STATUS_COLORS,
  PRIORITY_DOT,
  TASK_STATUS_ORDER,
  type TaskFilters,
  type TaskStatus,
  type SubTask,
  type InternalUser,
  type Task,
} from "@/features/tasks/types";

// ── Session persistence ────────────────────────────────────────────────────────

const FRIDAY_LIST_SESSION_KEY = "friday.list.filters";

function readFridayListSession(): TaskFilters {
  try {
    const raw = sessionStorage.getItem(FRIDAY_LIST_SESSION_KEY);
    return raw ? (JSON.parse(raw) as TaskFilters) : {};
  } catch {
    return {};
  }
}

// ── Color palettes ─────────────────────────────────────────────────────────────

function stableTaskColor(id: string, palette: readonly string[]): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

const TASK_ROW_COLORS = [
  "bg-rose-400",
  "bg-amber-400",
  "bg-emerald-400",
  "bg-sky-400",
  "bg-violet-400",
  "bg-orange-400",
  "bg-teal-400",
  "bg-pink-400",
  "bg-indigo-400",
  "bg-lime-500",
] as const;

const GROUP_COLORS = [
  { text: "text-rose-600 dark:text-rose-400", border: "border-l-rose-500", bg: "bg-rose-50 dark:bg-rose-900/10" },
  { text: "text-amber-600 dark:text-amber-400", border: "border-l-amber-500", bg: "bg-amber-50 dark:bg-amber-900/10" },
  { text: "text-emerald-600 dark:text-emerald-400", border: "border-l-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-900/10" },
  { text: "text-sky-600 dark:text-sky-400", border: "border-l-sky-500", bg: "bg-sky-50 dark:bg-sky-900/10" },
  { text: "text-violet-600 dark:text-violet-400", border: "border-l-violet-500", bg: "bg-violet-50 dark:bg-violet-900/10"},
  { text: "text-orange-600 dark:text-orange-400", border: "border-l-orange-500", bg: "bg-orange-50 dark:bg-orange-900/10"},
] as const;

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatTimeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  if (months >= 1) return `${months} mies. temu`;
  if (weeks >= 1) return `${weeks} tyg. temu`;
  if (days >= 1) return `${days} ${days === 1 ? "dzień" : "dni"} temu`;
  const hours = Math.floor(diff / 3600000);
  if (hours >= 1) return `${hours} godz. temu`;
  return "przed chwilą";
}

function formatDueDate(dateStr: string | null): { text: string; cls: string } | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const diff = Math.floor((date.getTime() - Date.now()) / 86400000);
  const text = formatDate(dateStr);
  const cls =
    diff < 0 ? "text-red-600 dark:text-red-400 font-semibold"
    : diff === 0 ? "text-orange-600 dark:text-orange-400 font-semibold"
    : diff <= 3 ? "text-amber-600 dark:text-amber-400"
    : "text-gray-500 dark:text-gray-400";
  return { text, cls };
}

function weekText(weeks: number | null): { text: string; cls: string } | null {
  if (weeks === null) return null;
  const text = weeks === 0 ? "0 tyg." : weeks > 0 ? `+${weeks} tyg.` : `${weeks} tyg.`;
  const cls =
    weeks < -1 ? "text-red-700 dark:text-red-400 font-semibold"
    : weeks === -1 ? "text-red-500 font-medium"
    : weeks === 0 ? "text-orange-600 font-semibold"
    : weeks === 1 ? "text-yellow-600"
    : "text-gray-400 dark:text-gray-500";
  return { text, cls };
}

function flattenSubtaskTree(
  parentId: string | null,
  all: SubTask[],
  depth = 0,
): { subtask: SubTask; depth: number }[] {
  const children = all
    .filter((s) => parentId === null ? !s.parent : s.parent === parentId)
    .sort((a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at));
  return children.flatMap((child) => [
    { subtask: child, depth },
    ...flattenSubtaskTree(child.id, all, depth + 1),
  ]);
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function TitleCell({
  taskId,
  title,
  colorClass,
  onOpen,
}: {
  taskId: string;
  title: string;
  colorClass: string;
  onOpen: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: update } = useUpdateTask(taskId);

  useEffect(() => { setVal(title); }, [title]);
  useEffect(() => { if (editing) inputRef.current?.select(); }, [editing]);

  function commit() {
    const trimmed = val.trim();
    if (trimmed && trimmed !== title) update({ title: trimmed });
    else setVal(title);
    setEditing(false);
  }

  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <span className={clsx("w-3 h-3 rounded-sm flex-shrink-0", colorClass)} />
      {editing ? (
        <input
          ref={inputRef}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") { setVal(title); setEditing(false); }
          }}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 min-w-0 text-sm font-medium bg-white dark:bg-gray-800 border border-[var(--color-primary)] rounded px-2 py-0 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        />
      ) : (
        <span
          onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
          onClick={onOpen}
          className="text-sm font-medium text-gray-800 dark:text-gray-200 hover:text-[var(--color-primary)] dark:hover:text-[var(--color-primary)] cursor-pointer truncate select-none"
          title="Kliknij — szczegóły · 2× kliknij — edytuj"
        >
          {title}
        </span>
      )}
    </div>
  );
}

function PeopleCell({
  taskId,
  assignees,
  users,
}: {
  taskId: string;
  assignees: Task["assignees"];
  users: InternalUser[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { mutate: assign } = useAssignUser(taskId);
  const { mutate: unassign } = useUnassignUser(taskId);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const assignedIds = new Set(assignees.map((a) => a.id));

  return (
    <div ref={ref} className="relative flex items-center gap-1">
      <div className="flex -space-x-1.5">
        {assignees.slice(0, 3).map((a) => (
          <UserAvatar key={a.id} userId={a.id} fullName={a.full_name} profilePictureUrl={a.profile_picture_url} size="sm" border />
        ))}
        {assignees.length > 3 && (
          <span className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 text-xs flex items-center justify-center text-gray-600 dark:text-gray-300 border-2 border-white dark:border-gray-800">
            +{assignees.length - 3}
          </span>
        )}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="w-5 h-5 rounded-full border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center hover:border-[var(--color-primary)] hover:bg-[var(--color-primary-dim)] dark:hover:bg-blue-900/30 transition-colors ml-0.5"
        title="Dodaj / usuń osobę"
      >
        <UserPlus size={10} className="text-gray-400 dark:text-gray-500" />
      </button>

      {open && (
        <div className="absolute left-0 top-8 z-30 w-52 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden">
          <p className="px-3 py-2 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-700">
            Przypisz osobę
          </p>
          <div className="max-h-52 overflow-y-auto">
            {users.map((u) => {
              const assigned = assignedIds.has(u.id);
              return (
                <div
                  key={u.id}
                  onClick={(e) => { e.stopPropagation(); assigned ? unassign(u.id) : assign(u.id); }}
                  className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                >
                  <UserAvatar userId={u.id} fullName={u.full_name} profilePictureUrl={u.profile_picture_url} size="sm" />
                  <span className="text-sm text-gray-700 dark:text-gray-300 flex-1 truncate">{u.full_name}</span>
                  {assigned && <Check size={13} className="text-[var(--color-primary)] flex-shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBar({ subs }: { subs: SubTask[] }) {
  if (subs.length === 0) return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;
  const total = subs.length;
  const done = subs.filter((s) => s.status === "done" || s.status === "cancelled").length;
  const active = subs.filter((s) => s.status === "todo_now").length;
  const waiting = subs.filter((s) => s.status === "waiting").length;
  const pct = Math.round((done / total) * 100);
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(done / total) * 100}%` }} />
        <div className="h-full bg-blue-500 transition-all" style={{ width: `${(active / total) * 100}%` }} />
        <div className="h-full bg-amber-400 transition-all" style={{ width: `${(waiting / total) * 100}%` }} />
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 w-7 text-right">{pct}%</span>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

const COL = "grid-cols-[4rem_1fr_148px_172px_96px]";
const CELL_BORDER = "border-r border-gray-200 dark:border-gray-700";

export function TasksListPage() {
  const [filters, setFilters] = useState<TaskFilters>(() => readFridayListSession());
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedSubTask, setSelectedSubTask] = useState<{ taskId: string; subtaskId: string } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [expandAllSet, setExpandAllSet] = useState<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const { data: tasks = [], isLoading } = useTasks(filters);
  const { data: allSubtasks = [] } = useAllSubTasks();
  const { data: projects = [] } = useProjects();
  const { data: users = [] } = useInternalUsers({ includeFridayContractors: true });
  const { data: clients = [] } = useClientsForTasks();
  const { data: categories = [] } = useTaskCategories();
  const { data: collapsedGroupKeys = [] } = useTaskGroupStates();
  const collapsedGroups = useMemo(() => new Set(collapsedGroupKeys), [collapsedGroupKeys]);
  const { mutate: reorderTasks } = useReorderTasks();
  const { mutate: saveExpanded } = useSetTaskExpanded();
  const { mutate: saveGroupCollapsed } = useSetGroupCollapsed();

  useEffect(() => {
    try { sessionStorage.setItem(FRIDAY_LIST_SESSION_KEY, JSON.stringify(filters)); } catch { /* ignore */ }
  }, [filters]);

  // One-time init of expanded tasks from server data
  useEffect(() => {
    if (!isLoading && tasks.length > 0 && !initializedRef.current) {
      initializedRef.current = true;
      setExpandedTasks(new Set(tasks.filter((t) => t.is_expanded).map((t) => t.id)));
    }
  }, [isLoading, tasks]);

  const dragTaskId = useRef<string | null>(null);
  const [overTaskId, setOverTaskId] = useState<string | null>(null);

  function handleTaskDrop(groupTasks: Task[]) {
    const fromId = dragTaskId.current;
    const toId = overTaskId;
    if (!fromId || !toId || fromId === toId) return;
    const ids = groupTasks.map((t) => t.id);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(toId);
    if (fromIdx === -1 || toIdx === -1) return;
    const newIds = [...ids];
    newIds.splice(fromIdx, 1);
    newIds.splice(toIdx, 0, fromId);
    reorderTasks(newIds);
  }

  const subtasksByTask = useMemo(
    () =>
      allSubtasks.reduce<Record<string, SubTask[]>>((acc, st) => {
        if (st.parent) return acc; // show only root subtasks in the table
        if (!acc[st.task]) acc[st.task] = [];
        acc[st.task].push(st);
        return acc;
      }, {}),
    [allSubtasks],
  );

  const allSubtasksByTask = useMemo(
    () =>
      allSubtasks.reduce<Record<string, SubTask[]>>((acc, st) => {
        if (!acc[st.task]) acc[st.task] = [];
        acc[st.task].push(st);
        return acc;
      }, {}),
    [allSubtasks],
  );

  // Group by project — key is project UUID (or "" for no project)
  const groups = useMemo(() => {
    const map = new Map<string, { key: string; label: string; tasks: Task[] }>();
    tasks.forEach((task) => {
      const key = task.project ?? "";
      const label = task.project_name ?? "Ogólne";
      if (!map.has(key)) map.set(key, { key, label, tasks: [] });
      map.get(key)!.tasks.push(task);
    });
    return Array.from(map.values());
  }, [tasks]);

  const taskColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    tasks.forEach((t) => { map[t.id] = stableTaskColor(t.id, TASK_ROW_COLORS); });
    return map;
  }, [tasks]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      const nowExpanded = !next.has(id);
      nowExpanded ? next.add(id) : next.delete(id);
      saveExpanded({ id, is_expanded: nowExpanded });
      if (!nowExpanded) setExpandAllSet((p: Set<string>) => { const n = new Set(p); n.delete(id); return n; });
      return next;
    });
  }, [saveExpanded]);

  const toggleExpandAll = useCallback((id: string) => {
    setExpandAllSet((prev) => {
      const next = new Set(prev);
      const wasActive = next.has(id);
      wasActive ? next.delete(id) : next.add(id);
      if (wasActive) {
        setExpandedTasks((p: Set<string>) => { const n = new Set(p); n.delete(id); return n; });
        saveExpanded({ id, is_expanded: false });
      } else {
        setExpandedTasks((p: Set<string>) => { if (p.has(id)) return p; const n = new Set(p); n.add(id); return n; });
        saveExpanded({ id, is_expanded: true });
      }
      return next;
    });
  }, [saveExpanded]);

  const toggleGroup = useCallback((projectKey: string) => {
    const nowCollapsed = !collapsedGroups.has(projectKey);
    saveGroupCollapsed({ project_key: projectKey, is_collapsed: nowCollapsed });
  }, [saveGroupCollapsed, collapsedGroups]);

  function groupByStatus(subs: SubTask[]): [TaskStatus, SubTask[]][] {
    const map = new Map<TaskStatus, SubTask[]>();
    TASK_STATUS_ORDER.forEach((s) => map.set(s, []));
    subs.forEach((st) => map.get(st.status)?.push(st));
    return Array.from(map.entries()).filter(([, items]) => items.length > 0);
  }

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Friday — Lista</h1>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)]"
        >
          <Plus size={16} />
          Nowe zadanie
        </button>
      </div>

      {/* Postęp prac — widoki przeniesione z menu bocznego (badge rozdzielone). */}
      <WorkProgressSection />

      {/* Filters */}
      <div className="flex flex-wrap gap-3 flex-shrink-0">
        <input
          placeholder="Szukaj po tytule..."
          value={filters.search ?? ""}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value || undefined }))}
          className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] w-full sm:w-52"
        />
        <SearchableSelect
          placeholder="Przedsięwzięcie: wszystkie"
          value={filters.project?.[0] ?? ""}
          onChange={(e) => setFilters((f) => ({ ...f, project: e.target.value ? [e.target.value] : [] }))}
          options={projects.map((p) => ({ value: p.id, label: p.short_name }))}
        />
        <SearchableSelect
          placeholder="Klient: wszyscy"
          value={filters.client?.[0] ?? ""}
          onChange={(e) => setFilters((f) => ({ ...f, client: e.target.value ? [e.target.value] : [] }))}
          options={clients.map((c) => ({ value: c.id, label: c.name }))}
        />
        <SearchableSelect
          placeholder="Pracownik: wszyscy"
          value={filters.assigned_to?.[0] ?? ""}
          onChange={(e) => setFilters((f) => ({ ...f, assigned_to: e.target.value ? [e.target.value] : [] }))}
          options={users.map((u) => ({ value: u.id, label: u.full_name }))}
        />
        <CategoryFilter
          categories={categories}
          activeFilter={
            filters.no_category ? "none" : filters.category
          }
          onFilterChange={(val) => {
            if (val === undefined) {
              setFilters((f) => { const { category, no_category, ...rest } = f; return rest; });
            } else if (val === "none") {
              setFilters((f) => { const { category, ...rest } = f; return { ...rest, no_category: true }; });
            } else {
              setFilters((f) => { const { no_category, ...rest } = f; return { ...rest, category: val }; });
            }
          }}
        />
        {Object.values(filters).some((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v))) && (
          <button onClick={() => setFilters({})} className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 px-2">
            Wyczyść
          </button>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-[520px]">
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : tasks.length === 0 ? (
          <div className="text-gray-400 dark:text-gray-500 text-sm py-16 text-center">Brak zadań pasujących do filtrów.</div>
        ) : (
          <>
            {/* Column headers */}
            <div className={clsx("grid sticky top-0 z-10", COL, "bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700")}>
              <div className={CELL_BORDER} />
              <div className={clsx("px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide", CELL_BORDER)}>Projekt</div>
              <div className={clsx("px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide", CELL_BORDER)}>People</div>
              <div className={clsx("px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide", CELL_BORDER)}>Status</div>
              <div className="px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Termin</div>
            </div>

            {groups.map((group, gi) => {
              const gc = GROUP_COLORS[gi % GROUP_COLORS.length];
              const collapsed = collapsedGroups.has(group.key);

              return (
                <div key={group.key}>
                  {/* Group header */}
                  <div
                    className={clsx("flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700 border-l-4 cursor-pointer select-none", gc.border, gc.bg)}
                    onClick={() => toggleGroup(group.key)}
                  >
                    {collapsed
                      ? <ChevronRight size={14} className={gc.text} />
                      : <ChevronDown size={14} className={gc.text} />
                    }
                    <span className={clsx("text-sm font-bold", gc.text)}>{group.label}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                      {group.tasks.length} {group.tasks.length === 1 ? "zadanie" : "zadań"}
                    </span>
                  </div>

                  {!collapsed && (
                    <>
                      {group.tasks.map((task) => {
                        const subs = subtasksByTask[task.id] ?? [];
                        const isExpanded = expandedTasks.has(task.id);
                        const isExpandAll = expandAllSet.has(task.id);
                        const color = taskColorMap[task.id] ?? TASK_ROW_COLORS[0];
                        const statusGroups = groupByStatus(subs);
                        const due = formatDueDate(task.due_date);

                        return (
                          <div key={task.id}>
                            {/* Task row */}
                            <div
                              draggable
                              onDragStart={() => { dragTaskId.current = task.id; }}
                              onDragOver={(e) => { e.preventDefault(); setOverTaskId(task.id); }}
                              onDrop={() => { handleTaskDrop(group.tasks); dragTaskId.current = null; setOverTaskId(null); }}
                              onDragEnd={() => { dragTaskId.current = null; setOverTaskId(null); }}
                              className={clsx(
                                "grid border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50/80 dark:hover:bg-gray-700/20 group/row transition-colors",
                                COL,
                                overTaskId === task.id && "border-t-2 border-t-blue-400",
                              )}
                            >
                              {/* Drag handle + Expand */}
                              <div className={clsx("flex items-center justify-center gap-0.5", CELL_BORDER)}>
                                <GripVertical
                                  size={13}
                                  className="text-gray-200 dark:text-gray-700 group-hover/row:text-gray-400 dark:group-hover/row:text-gray-500 cursor-grab flex-shrink-0"
                                />
                                {task.subtask_count > 0 ? (
                                  <>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleExpand(task.id); }}
                                      className="text-gray-400 dark:text-gray-500 group-hover/row:text-gray-600 dark:group-hover/row:text-gray-400 hover:text-[var(--color-primary)] flex-shrink-0"
                                      title={isExpanded ? "Zwiń podzadania" : "Rozwiń podzadania"}
                                    >
                                      {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleExpandAll(task.id); }}
                                      className={clsx(
                                        "flex-shrink-0 transition-colors",
                                        isExpandAll
                                          ? "text-[var(--color-primary)]"
                                          : "text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]",
                                      )}
                                      title={isExpandAll ? "Zwiń całe drzewo" : "Rozwiń całe drzewo podzadań"}
                                    >
                                      <span className={`flex items-center transition-transform ${isExpanded ? "rotate-90" : ""}`}>
                                        <ChevronRight size={11} />
                                        <ChevronRight size={11} className="-ml-2" />
                                      </span>
                                    </button>
                                  </>
                                ) : (
                                  <span className="w-7 block flex-shrink-0" />
                                )}
                              </div>

                              {/* Title */}
                              <div className={clsx("flex items-center gap-2 px-3 py-2.5 min-w-0", CELL_BORDER)}>
                                <TitleCell
                                  taskId={task.id}
                                  title={task.title}
                                  colorClass={color}
                                  onOpen={() => setSelectedTaskId(task.id)}
                                />
                                {task.my_category && (
                                  <span
                                    className="flex-shrink-0 flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full font-medium"
                                    style={{
                                      backgroundColor: task.my_category.color + "22",
                                      color: task.my_category.color,
                                      border: `1px solid ${task.my_category.color}44`,
                                    }}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: task.my_category.color }} />
                                    {task.my_category.name}
                                  </span>
                                )}
                                {subs.length > 0 && (
                                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                                    {subs.filter((s) => s.status === "done").length}/{subs.length}
                                  </span>
                                )}
                                {task.comments_count > 0 && (
                                  <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0" title="Komentarze do zadania">
                                    <MessageSquare size={11} />
                                    <span>{task.comments_count}</span>
                                    {task.last_comment_at && (
                                      <span className="text-gray-300 dark:text-gray-600 hidden xl:inline">· {formatTimeAgo(task.last_comment_at)}</span>
                                    )}
                                  </span>
                                )}
                              </div>

                              {/* People */}
                              <div className={clsx("flex items-center px-3 py-2.5", CELL_BORDER)}>
                                <PeopleCell taskId={task.id} assignees={task.assignees} users={users} />
                              </div>

                              {/* Status bar */}
                              <div className={clsx("flex items-center px-3 py-2.5", CELL_BORDER)}>
                                <StatusBar subs={subs} />
                              </div>

                              {/* Due date */}
                              <div className="flex items-center px-3 py-2.5">
                                {due
                                  ? <span className={clsx("text-xs", due.cls)}>{due.text}</span>
                                  : <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                                }
                              </div>
                            </div>

                            {/* Subtask rows */}
                            {isExpanded && subs.length > 0 && (
                              <div className="bg-gray-50/60 dark:bg-gray-800/30">
                                {isExpandAll ? (
                                  // Tree view — full hierarchy, depth-indented
                                  flattenSubtaskTree(null, allSubtasksByTask[task.id] ?? []).map(({ subtask: st, depth }) => {
                                    const wk = weekText(st.weeks_until_due);
                                    return (
                                      <div
                                        key={st.id}
                                        onClick={() => setSelectedSubTask({ taskId: task.id, subtaskId: st.id })}
                                        className={clsx("grid border-b border-gray-100 dark:border-gray-700/50 hover:bg-[var(--color-primary-dim)]/60 dark:hover:bg-blue-900/10 cursor-pointer transition-colors", COL)}
                                      >
                                        <div className={CELL_BORDER} />
                                        <div
                                          className={clsx("flex items-center gap-2 py-2 pr-3 min-w-0", CELL_BORDER)}
                                          style={{ paddingLeft: `${36 + depth * 14}px` }}
                                        >
                                          <span className={clsx("w-1.5 h-1.5 rounded-full flex-shrink-0", PRIORITY_DOT[st.priority])} />
                                          <span className={clsx("text-xs truncate flex-1", st.status === "done" ? "line-through text-gray-400 dark:text-gray-500" : "text-gray-700 dark:text-gray-300")}>
                                            {st.title}
                                          </span>
                                          {st.comments_count > 0 && (
                                            <span className="flex items-center gap-0.5 text-xs text-gray-400 flex-shrink-0">
                                              <MessageSquare size={10} />
                                              {st.comments_count}
                                            </span>
                                          )}
                                        </div>
                                        <div className={clsx("flex items-center px-3 py-2", CELL_BORDER)}>
                                          <div className="flex -space-x-1">
                                            {st.assignees.slice(0, 3).map((a) => (
                                              <UserAvatar key={a.id} userId={a.id} fullName={a.full_name} profilePictureUrl={a.profile_picture_url} size="sm" border />
                                            ))}
                                          </div>
                                        </div>
                                        <div className={clsx("flex items-center px-3 py-2", CELL_BORDER)}>
                                          <span className={clsx("text-xs px-2 py-0.5 rounded-full", TASK_STATUS_COLORS[st.status])}>
                                            {TASK_STATUS_LABELS[st.status]}
                                          </span>
                                        </div>
                                        <div className="flex items-center px-3 py-2">
                                          {wk && <span className={clsx("text-xs", wk.cls)}>{wk.text}</span>}
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  // Status-grouped flat view (default)
                                  statusGroups.map(([status, items]) => (
                                    <div key={status}>
                                      <div className={clsx("grid border-b border-gray-100 dark:border-gray-700/60", COL)}>
                                        <div className={CELL_BORDER} />
                                        <div className="col-span-4 px-3 py-1.5 pl-9 flex items-center gap-2">
                                          <span className={clsx("text-xs px-2 py-0.5 rounded-full font-medium", TASK_STATUS_COLORS[status])}>
                                            {TASK_STATUS_LABELS[status]}
                                          </span>
                                          <span className="text-xs text-gray-400 dark:text-gray-500">({items.length})</span>
                                        </div>
                                      </div>
                                      {items.map((st) => {
                                        const wk = weekText(st.weeks_until_due);
                                        return (
                                          <div
                                            key={st.id}
                                            onClick={() => setSelectedSubTask({ taskId: task.id, subtaskId: st.id })}
                                            className={clsx("grid border-b border-gray-100 dark:border-gray-700/50 hover:bg-[var(--color-primary-dim)]/60 dark:hover:bg-blue-900/10 cursor-pointer transition-colors", COL)}
                                          >
                                            <div className={CELL_BORDER} />
                                            <div className={clsx("flex items-center gap-2 px-3 py-2 pl-9 min-w-0", CELL_BORDER)}>
                                              <span className={clsx("w-1.5 h-1.5 rounded-full flex-shrink-0", PRIORITY_DOT[st.priority])} />
                                              <span className={clsx("text-xs truncate flex-1", st.status === "done" ? "line-through text-gray-400 dark:text-gray-500" : "text-gray-700 dark:text-gray-300")}>
                                                {st.title}
                                              </span>
                                              {st.children_count > 0 && (
                                                <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-full" title="Zawiera podzadania">
                                                  +{st.children_count}
                                                </span>
                                              )}
                                              {st.comments_count > 0 && (
                                                <span className="flex items-center gap-0.5 text-xs text-gray-400 flex-shrink-0">
                                                  <MessageSquare size={10} />
                                                  {st.comments_count}
                                                </span>
                                              )}
                                            </div>
                                            <div className={clsx("flex items-center px-3 py-2", CELL_BORDER)}>
                                              <div className="flex -space-x-1">
                                                {st.assignees.slice(0, 3).map((a) => (
                                                  <UserAvatar key={a.id} userId={a.id} fullName={a.full_name} profilePictureUrl={a.profile_picture_url} size="sm" border />
                                                ))}
                                              </div>
                                            </div>
                                            <div className={clsx("flex items-center px-3 py-2", CELL_BORDER)}>
                                              <span className={clsx("text-xs px-2 py-0.5 rounded-full", TASK_STATUS_COLORS[st.status])}>
                                                {TASK_STATUS_LABELS[st.status]}
                                              </span>
                                            </div>
                                            <div className="flex items-center px-3 py-2">
                                              {wk && <span className={clsx("text-xs", wk.cls)}>{wk.text}</span>}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* + Dodaj zadanie w tej grupie */}
                      <div className={clsx("border-b border-gray-200 dark:border-gray-700", gc.bg)}>
                        <button
                          onClick={() => setCreateOpen(true)}
                          className={clsx("flex items-center gap-1 text-xs px-3 py-2 hover:opacity-70 transition-opacity", gc.text)}
                        >
                          <Plus size={12} />
                          Dodaj zadanie
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </>
        )}
        </div>
      </div>

      {selectedTaskId && (
        <TaskDetailPanel taskId={selectedTaskId} onClose={() => setSelectedTaskId(null)} />
      )}
      {selectedSubTask && (
        <SubTaskDetailPanel
          taskId={selectedSubTask.taskId}
          subtaskId={selectedSubTask.subtaskId}
          onClose={() => setSelectedSubTask(null)}
        />
      )}
      <CreateTaskModal open={createOpen} onClose={() => setCreateOpen(false)} projects={projects} />
    </div>
  );
}
